import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, Save, X, Search, Copy, CheckCircle2, Repeat, Sparkles, Link as LinkIcon, ExternalLink, Users as UsersIcon } from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY = {
  code: "", name: "", email: "", phone: "", business_name: "",
  default_rate_pct: 10, active: true, syncro_referrer_label: "", notes: "",
  // If this partner runs a transfer flow (i.e. ports customers over from
  // a specific upstream RSP), the upstream provider's public name goes
  // here. Drives banner + link copy end-to-end. Leave blank for partners
  // that only ever take new customers.
  transfer_upstream_name: "",
  // Extra visible-CC emails looped in on every partner-facing send
  // (Kanban "Email partner" note, monthly payout PDF, stage-change
  // automation emails). The primary `email` above is always To.
  cc_emails: [],
  // Dedicated payout-statement recipient. When set, the monthly
  // commission PDF goes here INSTEAD of the main `email` and the
  // cc_emails list is skipped so general staff never see payout
  // figures. Blank = fall back to the main email.
  payout_email: "",
};

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [teamCounts, setTeamCounts] = useState({}); // { partner_code: count }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // partner or {new: true}
  const [inviting, setInviting] = useState(null);
  const [syncroLookup, setSyncroLookup] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      // Users pulled in parallel so the "N team" chip on each row is
      // populated on first paint. Endpoint is admin-only; failure just
      // hides the chip (rest of the page still renders).
      const [{ data }, usersRes] = await Promise.all([
        api.get("/commissions/partners"),
        api.get("/commissions/users").catch(() => ({ data: { users: [] } })),
      ]);
      setPartners(data);
      const counts = {};
      for (const u of (usersRes.data.users || [])) {
        if (u.partner_code) counts[u.partner_code] = (counts[u.partner_code] || 0) + 1;
      }
      setTeamCounts(counts);
    } catch (_) {
      toast.error("Failed to load partners");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  async function save(form) {
    try {
      if (form.__editing_code) {
        await api.put(`/commissions/partners/${form.__editing_code}`, form);
      } else {
        await api.post(`/commissions/partners`, form);
      }
      toast.success("Partner saved");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  }

  async function remove(code) {
    if (!window.confirm(`Delete partner "${code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/commissions/partners/${code}`);
      toast.success("Partner deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-500 font-semibold mb-3">Partners</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium">Referral partners</h1>
          <p className="text-slate-400 mt-2">Manage who earns commission. Actual rates per product live in <span className="text-cyan-400 font-medium">Settings → Commission rules</span>.</p>
        </div>
        <button
          data-testid="add-partner-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all hover:-translate-y-[1px]"
        >
          <UserPlus className="w-4 h-4" /> Add partner
        </button>
      </header>

      <div className="rounded-xl border border-slate-800/80 bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-6 py-3">Code</th>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Syncro label</th>
              <th className="text-right px-6 py-3" title="Applied only to line items that don't match any commission rule">Fallback rate</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-center px-6 py-3">Links</th>
              <th className="text-right px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100" data-testid="partners-tbody">
            {loading && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr>}
            {!loading && partners.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-500">No partners yet.</td></tr>
            )}
            {partners.map((p) => (
              <tr key={p.code} className="hover:bg-slate-950/50">
                <td className="px-6 py-4 font-mono text-xs text-slate-300">{p.code}</td>
                <td className="px-6 py-4 font-medium">
                  <Link
                    to={`/admin/partners/${encodeURIComponent(p.code)}`}
                    data-testid={`partner-name-link-${p.code}`}
                    className="text-slate-100 hover:text-cyan-300 hover:underline underline-offset-2 decoration-cyan-500/50 transition-colors"
                    title={`Open ${p.name} — customers, invoices, commission rollup`}
                  >
                    {p.name}
                  </Link>
                </td>
                <td className="px-6 py-4 text-slate-400">{p.email || <span className="text-slate-500">—</span>}</td>
                <td className="px-6 py-4 text-slate-400 text-xs font-mono">{p.syncro_referrer_label || <span className="text-slate-500">—</span>}</td>
                <td className="px-6 py-4 text-right">
                  <span className="font-mono text-slate-400 text-xs">{p.default_rate_pct}%</span>
                  <div className="text-[10px] text-slate-500 mt-0.5">unmatched only</div>
                </td>
                <td className="px-6 py-4">
                  {p.active
                    ? <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-cyan-950 text-cyan-400 border border-cyan-900">Active</span>
                    : <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Inactive</span>}
                </td>
                <td className="px-6 py-4 text-center">
                  <SignupLinkChips partnerCode={p.code} partnerName={p.name} transferUpstream={p.transfer_upstream_name} />
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <Link
                    to={`/admin/partners/${encodeURIComponent(p.code)}`}
                    data-testid={`view-partner-${p.code}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 font-semibold transition-colors"
                    title="Open partner rollup — customers, invoices, commission history"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View
                  </Link>
                  <button
                    data-testid={`syncro-lookup-${p.code}`}
                    onClick={() => setSyncroLookup(p)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-800 hover:bg-slate-950 transition-colors"
                    title="Find this partner in Syncro"
                  >
                    <Search className="w-3.5 h-3.5" /> Find in Syncro
                  </button>
                  <button
                    data-testid={`invite-${p.code}`}
                    onClick={() => setInviting(p)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-800 hover:bg-slate-950 transition-colors"
                    title="Create partner login"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Invite
                  </button>
                  {p.login_email && (
                    <ResendInviteButton partner={p} />
                  )}
                  <Link
                    to={`/admin/users?partner=${encodeURIComponent(p.code)}`}
                    data-testid={`team-view-${p.code}`}
                    title={`View ${teamCounts[p.code] || 0} user${(teamCounts[p.code] || 0) === 1 ? "" : "s"} under ${p.name} — reset lockouts, issue password links, disable staff`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-800 hover:border-cyan-500/40 hover:bg-cyan-500/10 hover:text-cyan-300 text-slate-300 transition-colors"
                  >
                    <UsersIcon className="w-3.5 h-3.5" />
                    Team ({teamCounts[p.code] || 0})
                  </Link>
                  <button
                    data-testid={`edit-${p.code}`}
                    onClick={() => setEditing({ ...p, __editing_code: p.code })}
                    className="text-xs text-cyan-400 hover:text-cyan-300 font-medium"
                  >Edit</button>
                  <button
                    data-testid={`delete-${p.code}`}
                    onClick={() => remove(p.code)}
                    className="text-xs text-rose-600 hover:text-rose-700 font-medium inline-flex items-center gap-1"
                  ><Trash2 className="w-3 h-3" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <PartnerModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
      {inviting && <InviteModal partner={inviting} onClose={() => setInviting(null)} onDone={reload} />}
      {syncroLookup && <SyncroLookupModal partner={syncroLookup} onClose={() => setSyncroLookup(null)} onPick={save} />}
    </div>
  );
}

function SignupLinkChips({ partnerCode, partnerName, transferUpstream }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(null);
  const origin = (process.env.REACT_APP_PUBLIC_ORIGIN
    || process.env.REACT_APP_BACKEND_URL
    || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const base = `${origin}/signup/${encodeURIComponent(partnerCode)}`;

  // Transfer-link copy reflects the upstream provider each partner ports
  // FROM (set on Admin → Partners → Edit → "Transfer upstream provider").
  // Falls back to "to {partner}" if no upstream is configured, which is
  // less useful than the previous hardcoded map but at least never lies.
  const upstream = (transferUpstream || "").trim();
  const transferLabel = upstream
    ? `Transfers from ${upstream} only!`
    : `Transfers to ${partnerName || partnerCode} only!`;

  async function copy(flow, url) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(flow);
      toast.success(`${flow === "transfer" ? transferLabel.replace(/!$/, "") : "New Connections"} link copied`);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error("Copy failed"); }
  }

  const chips = [
    { key: "transfer", url: `${base}/transfer`,
      label: transferLabel, Icon: Repeat },
    { key: "new", url: `${base}/new`,
      label: "New Connections", Icon: Sparkles },
  ];

  // Close popover on outside click. Simple listener — no ref needed since
  // the parent element has data-testid we can scope to.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest?.(`[data-testid="signup-links-${partnerCode}"]`)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, partnerCode]);

  return (
    <div className="relative inline-block" data-testid={`signup-links-${partnerCode}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`signup-links-toggle-${partnerCode}`}
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
          open
            ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-200"
            : "border-slate-800 hover:bg-slate-950 text-slate-300"
        }`}
        title="Show partner signup URLs"
      >
        <LinkIcon className="w-3.5 h-3.5" /> Links
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-30 w-[340px] rounded-xl border border-slate-800 bg-slate-950 shadow-2xl p-3"
          role="menu"
          data-testid={`signup-links-popover-${partnerCode}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 px-1">
            {partnerName || partnerCode} · signup URLs
          </div>
          <div className="space-y-2">
            {chips.map(({ key, url, label, Icon }) => (
              <div key={key} className="rounded-lg border border-slate-800 bg-slate-900 p-2.5">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-100 mb-1.5">
                  <Icon className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                  <span className="truncate">{label}</span>
                </div>
                <div className="flex items-stretch rounded-md border border-slate-800 bg-slate-950 overflow-hidden">
                  <div className="flex-1 min-w-0 px-2 py-1 font-mono text-[10px] text-cyan-200 truncate" title={url}>
                    {url}
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(key, url)}
                    data-testid={`copy-${key}-${partnerCode}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2 border-l border-slate-800 hover:bg-slate-900 text-[10px] font-semibold text-slate-200"
                  >
                    {copied === key ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    {copied === key ? "Copied" : "Copy"}
                  </button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center px-2 border-l border-slate-800 hover:bg-slate-900 text-slate-200"
                    title="Preview in a new tab"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SyncroLookupModal({ partner, onClose, onPick }) {
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/commissions/partners/${partner.code}/lookup-syncro`);
        setCandidates(data.candidates || []);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed");
      } finally { setBusy(false); }
    })();
  }, []);

  async function pick(cand) {
    // Save the syncro_customer_id AND (if not set) the referrer label from Syncro
    await onPick({
      ...partner,
      __editing_code: partner.code,
      syncro_customer_id: cand.id,
      syncro_referrer_label: partner.syncro_referrer_label || cand.referred_by || partner.name,
    });
    onClose();
  }

  return (
    <Modal onClose={onClose} title={`Find "${partner.name}" in Syncro`} testid="syncro-lookup-modal">
      {busy && <div className="text-sm text-slate-500 py-6">Searching Syncro…</div>}
      {error && <div className="text-sm text-rose-600 py-4">{error}</div>}
      {!busy && !error && candidates?.length === 0 && (
        <div className="text-sm text-slate-500 py-4">
          No candidates found in Syncro for this partner. Try adding an email or business name that matches your Syncro customer record.
        </div>
      )}
      {!busy && !error && candidates?.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="syncro-candidates">
          {candidates.map((c) => (
            <button
              key={c.id}
              data-testid={`pick-syncro-${c.id}`}
              onClick={() => pick(c)}
              className="w-full text-left rounded-lg border border-slate-800 hover:border-cyan-500 hover:bg-cyan-950/40 p-4 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{c.business_name || c.fullname || `Customer #${c.id}`}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.email || "no email"} · matched by &quot;{c.match_query}&quot;</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-xs text-slate-500">Syncro #{c.id}</div>
                  {c.referred_by && (
                    <div className="text-xs text-cyan-400 mt-1">Referred by: <b>{c.referred_by}</b></div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function CcEmailChips({ values, primaryEmail, onChange }) {
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const primary = (primaryEmail || "").trim().toLowerCase();
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function commit(rawInput) {
    // Split on comma/semicolon/whitespace so paste-a-block-of-addresses
    // just works. Filter invalid + dedupe against the existing list +
    // primary email. Cap at 10 to match the backend limit.
    const parts = String(rawInput || "")
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) { setPending(""); return; }
    const existing = new Set([primary, ...values.map((v) => v.toLowerCase())].filter(Boolean));
    const added = [];
    const invalid = [];
    for (const p of parts) {
      const k = p.toLowerCase();
      if (existing.has(k)) continue;
      if (!EMAIL_RE.test(p)) { invalid.push(p); continue; }
      added.push(p);
      existing.add(k);
    }
    const next = [...values, ...added].slice(0, 10);
    onChange(next);
    setPending("");
    if (invalid.length) {
      setError(`Not a valid email: ${invalid.join(", ")}`);
      setTimeout(() => setError(""), 3500);
    }
  }

  function remove(email) {
    onChange(values.filter((v) => v !== email));
  }

  function onKeyDown(e) {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commit(pending);
    } else if (e.key === "Backspace" && !pending && values.length) {
      // Peel the last chip when the input is empty and user hits ⌫ — a
      // common tag-input affordance.
      onChange(values.slice(0, -1));
    }
  }

  return (
    <div>
      <div
        data-testid="partner-cc-chips"
        className="flex flex-wrap items-center gap-1.5 min-h-[42px] rounded-lg border border-slate-800 bg-slate-900 px-2 py-1.5 focus-within:border-cyan-500 focus-within:ring-2 focus-within:ring-cyan-500/20 transition-colors"
      >
        {values.map((email) => (
          <span
            key={email}
            data-testid={`partner-cc-chip-${email}`}
            className="inline-flex items-center gap-1 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 pl-2 pr-1 py-0.5 text-xs font-mono"
          >
            {email}
            <button
              type="button"
              onClick={() => remove(email)}
              data-testid={`partner-cc-remove-${email}`}
              aria-label={`Remove ${email}`}
              className="ml-0.5 rounded-sm p-0.5 hover:bg-cyan-500/20 text-cyan-300 hover:text-white transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          data-testid="partner-cc-input"
          type="email"
          value={pending}
          onChange={(e) => { setPending(e.target.value); if (error) setError(""); }}
          onKeyDown={onKeyDown}
          onBlur={() => pending && commit(pending)}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[,;\s]/.test(text)) {
              e.preventDefault();
              commit(text);
            }
          }}
          placeholder={values.length >= 10 ? "10 addresses (max)" : "email@example.com — press Enter"}
          disabled={values.length >= 10}
          className="flex-1 min-w-[180px] bg-transparent outline-none px-1.5 py-1 text-sm placeholder:text-slate-500 disabled:cursor-not-allowed"
        />
      </div>
      {error && (
        <div className="text-xs text-rose-400 mt-1" data-testid="partner-cc-error">{error}</div>
      )}
    </div>
  );
}

function PartnerModal({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial);
  function bind(k, transform) {
    return (e) => {
      const v = e.target.type === "checkbox" ? e.target.checked
        : e.target.type === "number" ? Number(e.target.value)
        : transform ? transform(e.target.value) : e.target.value;
      setF(x => ({ ...x, [k]: v }));
    };
  }
  return (
    <Modal onClose={onClose} title={initial.__editing_code ? "Edit partner" : "New partner"} testid="partner-modal" size="5xl">
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code (used internally)" required>
            <input
              data-testid="partner-code"
              disabled={!!initial.__editing_code}
              value={f.code} onChange={bind("code", v => v.toLowerCase().replace(/[^a-z0-9-_]/g, ""))}
              className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono disabled:bg-slate-950" />
          </Field>
          <Field label="Fallback rate (only for line items that match NO rule)">
            <div className="relative">
              <input data-testid="partner-rate" type="number" step="0.1" min="0" max="100" value={f.default_rate_pct} onChange={bind("default_rate_pct")}
                className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 pr-8 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
            </div>
            <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              Real commission rates come from <b className="text-slate-300">Settings → Commission rules</b> — per product line (NBN, Voice, Cloud, etc). This fallback only kicks in when a Syncro line item doesn&apos;t match any of your rules.
            </div>
          </Field>
        </div>
        <Field label="Business/display name" required>
          <input data-testid="partner-name" value={f.name} onChange={bind("name")}
            className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" />
        </Field>
        {/* Co-branding: shown on partner-scoped signup URLs so customers see
            the reseller's logo + colour + tagline alongside Steele Cloud. */}
        <fieldset className="rounded-lg border border-slate-800 p-4">
          <legend className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-2">
            Public co-branding · shown on /signup/{f.code || "{code}"}
          </legend>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3">
                <Field label="Logo URL (https://…) — square or wide, 400x120 works well">
                  <input data-testid="partner-logo-url" value={f.logo_url || ""} onChange={bind("logo_url")}
                    placeholder="https://cdn.example.com/macktronix-logo.png"
                    className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono text-sm" />
                </Field>
                <Field label="Brand colour (#hex)">
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="partner-brand-color-picker"
                      type="color"
                      value={f.brand_color || "#22d3ee"}
                      onChange={bind("brand_color")}
                      className="w-10 h-10 rounded-lg border border-slate-800 bg-slate-900 cursor-pointer"
                    />
                    <input
                      data-testid="partner-brand-color"
                      value={f.brand_color || ""}
                      onChange={bind("brand_color")}
                      placeholder="#22d3ee"
                      className="flex-1 rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono text-sm"
                    />
                  </div>
                </Field>
              </div>
              <Field label="Tagline (optional, shown under the wizard heading)">
                <input data-testid="partner-tagline" value={f.tagline || ""} onChange={bind("tagline")}
                  placeholder="Local IT support since 2005"
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm" />
              </Field>
              {/* Per-flow freeform intro copy — sanitised HTML block
                  rendered under the wizard H1 on the matching flow.
                  Lets a partner rewrite the transfer / new-connect
                  landing pitch without a code change. Supports
                  <b>, <a>, <ul>, <br> etc; scripts + event handlers
                  are stripped by DOMPurify on render. */}
              <Field label="Transfer signup intro (shown on /signup/{code}/transfer, HTML allowed)">
                <textarea data-testid="partner-signup-transfer-intro"
                  value={f.signup_transfer_intro_html || ""}
                  onChange={bind("signup_transfer_intro_html")}
                  placeholder="e.g. <b>Existing Valve customer?</b> Transfer to Macktronix NBN and keep your speeds — no new contract, no service downtime."
                  rows={3}
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm font-mono" />
              </Field>
              <Field label="New signup intro (shown on /signup/{code}/new, HTML allowed)">
                <textarea data-testid="partner-signup-new-intro"
                  value={f.signup_new_intro_html || ""}
                  onChange={bind("signup_new_intro_html")}
                  placeholder="e.g. <b>Fresh install?</b> Pick a plan below — we&rsquo;ll handle the connect with NBN Co and email you when the line is live."
                  rows={3}
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm font-mono" />
              </Field>
              <div className="text-[10px] text-slate-500 leading-relaxed pt-1">
                <b className="text-slate-400">Tips.</b> Logo max height ~48px in the header (auto-fits). Use a contrasting brand colour (WCAG AA vs dark bg ≥ 4.5:1) — avoid dark navy/black on our slate-950 background.
              </div>
            </div>
            <PartnerBrandingPreview partner={f} />
          </div>
        </fieldset>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email">
            <input data-testid="partner-email" type="email" value={f.email || ""} onChange={bind("email")}
              className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" />
          </Field>
          <Field label="Phone">
            <input data-testid="partner-phone" value={f.phone || ""} onChange={bind("phone")}
              className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" />
          </Field>
        </div>
        {/* Additional CC recipients — kept in the loop on every
            partner-facing send: Kanban "Email partner", monthly payout
            statement, and stage-change automation emails. Managed as
            chips so admins can drop one back to just the main email
            (e.g. operations@) with a single click. */}
        <Field label="Also Cc these people on partner emails">
          <CcEmailChips
            values={f.cc_emails || []}
            primaryEmail={f.email}
            onChange={(v) => setF((x) => ({ ...x, cc_emails: v }))}
          />
          <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
            Anyone here gets a <b className="text-slate-300">visible Cc</b> on every note/payout/stage email we send this partner. Up to 10 addresses. Leave empty to only email the main address above.
          </div>
        </Field>
        {/* Dedicated payout-statement recipient. Some partners run
            accounts separately from ops and don't want general staff
            seeing payout PDFs. When set, monthly payout emails go
            HERE only — the main email + cc_emails list is bypassed
            for payout sends (but still used for every other partner
            comm). */}
        <Field label="Payouts email — separate finance/accounts contact (optional)">
          <input
            data-testid="partner-payout-email"
            type="email"
            value={f.payout_email || ""}
            onChange={bind("payout_email")}
            placeholder="finance@partner.example"
            className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          />
          <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
            When set, monthly payout PDFs go <b className="text-slate-300">only</b> to this address (no Cc list, no main email) — perfect for partners whose accounts team is separate from ops. Leave blank to send payout statements to the main email above.
          </div>
        </Field>
        <Field label="Syncro Referrer label (exact match in Syncro admin → Customers → Referrers)">
          <input data-testid="partner-syncro-label" value={f.syncro_referrer_label || ""} onChange={bind("syncro_referrer_label")}
            className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono text-sm" />
        </Field>
        {/* Transfer flow config: name of the upstream RSP this partner
            ports customers FROM. Setting a value here unlocks:
              - "Transfers from X only!" copy in admin + partner portal
              - Amber "X customers only" pill on the flow picker
              - Warning banner on `/signup/{code}/transfer`
              - Optional AVC field on `/signup/{code}/new` (for customers
                porting in from a NON-{upstream} provider)
            Leave blank if this partner has no transfer flow — the
            wizard falls back to a plain "existing customers" copy. */}
        <Field label="Transfer upstream provider (e.g. “Valve”) — leave blank if no transfer flow">
          <input
            data-testid="partner-transfer-upstream"
            value={f.transfer_upstream_name || ""}
            onChange={bind("transfer_upstream_name")}
            placeholder="Valve"
            className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm"
          />
          <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
            The RSP name your customers are being ported <b>from</b>. Shown to customers on the transfer link + used to gate the AVC field on the new-customer link.
          </div>
        </Field>
        <Field label="Notes">
          <textarea data-testid="partner-notes" rows={2} value={f.notes || ""} onChange={bind("notes")}
            className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm" />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="partner-active" type="checkbox" checked={!!f.active} onChange={bind("active")} className="rounded" />
          Active (commissions accrue)
        </label>
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
          <button data-testid="partner-cancel" type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950">Cancel</button>
          <button data-testid="partner-save" type="submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium">
            <Save className="w-4 h-4" /> Save partner
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * "Resend invite" — reissues a fresh 24-hour set-password link for
 * a partner who lost/didn't receive the original invite email. Only
 * shown on rows where a login already exists (partner.login_email
 * is set) since there's nothing to resend otherwise. Shows the new
 * link inline as a fallback for admins whose SMTP/Graph isn't wired.
 */
function ResendInviteButton({ partner }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  async function trigger() {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/auth/invite-partner/resend", {
        email: partner.login_email,
      });
      if (!data.ok) {
        toast.error("No matching partner login found — invite them first.");
        return;
      }
      setResult(data);
      toast.success(data.invite_email_sent
        ? `Invite email resent to ${partner.login_email}`
        : `Invite link ready — copy it below`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    } finally { setBusy(false); }
  }
  async function copyLink() {
    if (!result?.invite_url) return;
    try {
      await navigator.clipboard.writeText(result.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Invite link copied");
    } catch { toast.error("Copy failed"); }
  }
  return (
    <>
      <button
        data-testid={`resend-invite-${partner.code}`}
        onClick={trigger}
        disabled={busy}
        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-800 hover:bg-slate-950 transition-colors disabled:opacity-60"
        title="Reissue a fresh 24h set-password link"
      >
        <Repeat className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Resend invite"}
      </button>
      {result?.invite_url && (
        <Modal onClose={() => setResult(null)} title="Invite resent" testid="resend-invite-modal">
          <div className="space-y-4">
            <div className="text-sm text-slate-200">
              {result.invite_email_sent
                ? <>A fresh 24-hour set-password link has been emailed to <b>{result.email}</b>.</>
                : <>Link ready — email delivery isn&apos;t configured, share it manually with <b>{result.email}</b>.</>}
            </div>
            <div className="rounded-lg border border-cyan-800 bg-cyan-950 p-3 flex items-start gap-2">
              <div className="flex-1 min-w-0 font-mono text-xs text-cyan-100 break-all select-all">
                {result.invite_url}
              </div>
              <button
                type="button"
                onClick={copyLink}
                data-testid="resend-invite-copy"
                className="shrink-0 px-2 py-1 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold inline-flex items-center gap-1"
              >
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button
              onClick={() => setResult(null)}
              className="px-4 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium"
            >Done</button>
          </div>
        </Modal>
      )}
    </>
  );
}


function InviteModal({ partner, onClose, onDone }) {
  const [email, setEmail] = useState(partner.email || "");
  const [name, setName] = useState(partner.name || "");
  const [password, setPassword] = useState("");
  // v1.10.28: owner (full commissions access) vs staff (no money).
  // v1.10.31: opt-in CC list membership — was auto-add for staff.
  const [role, setRole] = useState("partner");
  const [addToCc, setAddToCc] = useState(false);
  // v1.10.43: link-based invite. `result` carries whichever response
  // shape the backend returned (invite_url + email_sent flag OR the
  // legacy temp_password path when admin set a password manually).
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invite-partner", {
        partner_code: partner.code, email, name, role,
        add_to_cc: addToCc,
        password: password || undefined,
      });
      setResult(data);
      if (data.invite_url) {
        toast.success(data.invite_email_sent
          ? `Invite email sent to ${email}`
          : `Invite link ready — email not configured, copy the link below`);
      } else if (data.temp_password) {
        toast.success(`Login created — copy the temporary password.`);
      } else {
        toast.success(`Login created`);
        onDone(); onClose();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invite failed");
    } finally { setBusy(false); }
  }
  async function copyLink() {
    if (!result?.invite_url) return;
    try {
      await navigator.clipboard.writeText(result.invite_url);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  }
  return (
    <Modal onClose={onClose} title={`Invite ${partner.name}`} testid="invite-modal">
      {result?.invite_url ? (
        // Link-based invite — no password shown, partner clicks the
        // link in their inbox and sets their own password.
        <div className="space-y-4" data-testid="invite-link-panel">
          <div className="text-sm text-slate-200">
            {result.invite_email_sent ? (
              <>Invite email sent to <b>{email}</b>. They&apos;ll click the link, set their own password, and be dropped into their portal.</>
            ) : (
              <>Invite link ready. Email delivery isn&apos;t configured, so share this link directly with <b>{email}</b>.</>
            )}
          </div>
          <div className="rounded-lg border border-cyan-800 bg-cyan-950 p-3 flex items-start gap-2">
            <div className="flex-1 min-w-0 font-mono text-xs text-cyan-100 break-all select-all" data-testid="invite-link-url">
              {result.invite_url}
            </div>
            <button
              type="button"
              onClick={copyLink}
              data-testid="invite-link-copy"
              className="shrink-0 px-2 py-1 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold inline-flex items-center gap-1"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="text-[11px] text-slate-500">
            Link expires in 24 hours. If the invite email bounces or gets lost, click <b>Invite</b> again from the partner row to reissue.
          </div>
          <button onClick={() => { onDone(); onClose(); }} className="px-4 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium" data-testid="invite-done">Done</button>
        </div>
      ) : result?.temp_password ? (
        // Legacy path — only hit when the admin manually typed a
        // password on the form. Kept for out-of-band shares where
        // the admin wants total control.
        <div className="space-y-4">
          <div className="text-sm">Login created for <b>{email}</b>. Give the partner this password (shown once):</div>
          <div className="rounded-lg border border-cyan-800 bg-cyan-950 p-4 font-mono text-lg text-cyan-200 select-all" data-testid="temp-password">{result.temp_password}</div>
          <button onClick={() => { onDone(); onClose(); }} className="px-4 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium">Done</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email"><input data-testid="invite-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" /></Field>
          <Field label="Display name"><input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" /></Field>
          <Field label="Password (leave blank to email a set-password link)"><input data-testid="invite-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono" placeholder="Leave blank — recommended" /></Field>
          {/* v1.10.31 — opt-in CC list membership (was auto-add for
              partner_staff). Ticking loops the invitee into every
              partner-facing outbound email; leave unticked to grant
              portal access only. */}
          <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer select-none">
            <input
              data-testid="invite-add-cc"
              type="checkbox"
              checked={addToCc}
              onChange={(e) => setAddToCc(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="text-slate-200 font-medium">Also Cc on partner emails</span>
              <span className="block text-[11px] text-slate-500 leading-relaxed">Loop them into every note we send this partner (Kanban activity, payout PDFs, stage automations). Leave unticked to give login access only.</span>
            </span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950">Cancel</button>
            <button data-testid="invite-submit" type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium">
              {busy ? "Sending…" : (password ? "Create login" : "Send invite")}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function PartnerBrandingPreview({ partner }) {
  const brandColor = partner.brand_color || "#22d3ee";
  const name = partner.name || partner.business_name || partner.code || "Partner";
  const shortName = (partner.name || partner.code || "Partner").split(/\s+/)[0];
  return (
    <div
      className="rounded-lg border border-slate-800 bg-slate-950 p-4 flex flex-col gap-3 sticky top-0 self-start"
      data-testid="partner-branding-preview"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Live preview · /signup/{partner.code || "{code}"}
        </div>
        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 rounded px-1.5 py-0.5 font-semibold">
          Customer sees this
        </span>
      </div>
      <div className="rounded-md bg-slate-950 border border-slate-800/70 p-5">
        <div
          className="text-[9px] uppercase tracking-[0.22em] font-semibold"
          style={{ color: brandColor }}
        >
          Steele Cloud · {name}
        </div>
        {partner.logo_url ? (
          <img
            src={partner.logo_url}
            alt={name}
            className="h-10 mt-2 rounded-md bg-white/5 border border-slate-800 p-1 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="h-10 mt-2 rounded-md border border-dashed border-slate-800 flex items-center justify-center text-[10px] text-slate-600">
            No logo — add a URL above
          </div>
        )}
        <div className="font-display text-2xl tracking-tight mt-2 text-slate-100">Transfer your NBN</div>
        {partner.tagline && (
          <p className="text-xs text-slate-400 mt-1 italic">&ldquo;{partner.tagline}&rdquo;</p>
        )}
        <p className="text-[10px] text-slate-500 mt-2">
          Steele Cloud NBN — as offered by <b className="text-slate-300">{name}</b>. Any signup completed here is attributed to them.
        </p>
        <div className="mt-3 rounded-md border border-slate-800 bg-slate-900 p-2.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-100">100/20 Unlimited</div>
              <div className="text-[10px] text-slate-500">Sample plan card</div>
            </div>
            <div className="text-lg font-mono text-cyan-300">$99.95<span className="text-[9px] text-slate-500">/mo</span></div>
          </div>
          <span
            className="mt-2 inline-flex items-center gap-1 text-[8px] uppercase tracking-widest rounded px-1.5 py-0.5 font-bold border"
            style={{
              backgroundColor: `${brandColor}22`,
              borderColor: `${brandColor}88`,
              color: brandColor,
            }}
          >
            As offered by {shortName}
          </span>
        </div>
      </div>
      <ContrastBadge hex={brandColor} />
    </div>
  );
}

function ContrastBadge({ hex }) {
  function luminance(h) {
    const n = (h || "").replace("#", "");
    if (n.length !== 6) return null;
    const [r, g, b] = [0, 2, 4].map(i => parseInt(n.slice(i, i + 2), 16) / 255);
    const lin = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  }
  const bgLum = luminance("#020617");
  const fgLum = luminance(hex);
  if (fgLum == null) return null;
  const ratio = (Math.max(bgLum, fgLum) + 0.05) / (Math.min(bgLum, fgLum) + 0.05);
  const passesAA = ratio >= 4.5;
  const passesAAA = ratio >= 7;
  return (
    <div className={`text-[10px] rounded px-2 py-1.5 border flex items-center justify-between ${
      passesAAA ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
      : passesAA ? "border-amber-500/40 bg-amber-500/5 text-amber-300"
      : "border-red-500/40 bg-red-500/5 text-red-300"
    }`}>
      <span>Contrast on dark bg: <b className="font-mono">{ratio.toFixed(2)}:1</b></span>
      <span className="font-semibold uppercase tracking-widest text-[9px]">
        {passesAAA ? "AAA ✓" : passesAA ? "AA ✓" : "Fails AA"}
      </span>
    </div>
  );
}


function Modal({ children, onClose, title, testid, size = "lg" }) {
  const widths = { lg: "max-w-lg", xl: "max-w-2xl", "3xl": "max-w-3xl", "5xl": "max-w-5xl" };
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className={`w-full ${widths[size] || widths.lg} rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="font-display text-xl">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors" data-testid="modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );
}
